import React, { ReactElement, isValidElement, useState, useMemo } from 'react';
import { TableColumn, TableColumnProps } from './components/TableColumn';
import '../../../scss/controls/_table.scss';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Button, ButtonBorderRadius, ButtonTheme } from '../Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export enum TableHeaderTheme {
    White = 'white',
    Blue = 'blue',
    Red = 'red',
    Gray = 'gray',
    Yellow = 'yellow',
    Green = 'green',
    Transparent = 'transparent',
}

export enum TableRowTheme {
    White = 'white',
    Blue = 'blue',
    Red = 'red',
    Gray = 'gray',
    Yellow = 'yellow',
    Transparent = 'transparent',
}

export enum TableBorderStyle {
    None = 'noBorder',
    Solid = 'solidBorder',
}

export enum TableBorderRadius {
    None = 'noRadius',
    Small = 'smallRadius',
    Medium = 'mediumRadius',
}

export enum TableRowSeperatorStyle {
    None = 'noSeperator',
    Small = 'smallSeperator',
    Medium = 'mediumSeperator',
    Large = 'largeSeperator',
}

export type TableRow = Record<string, any>;

export const Table: React.FC<{
    children: ReactElement<typeof TableColumn> | ReactElement<typeof TableColumn>[];
    data: TableRow[];
    headerTheme: TableHeaderTheme;
    rowTheme: TableRowTheme;
    width?: string;
    height?: string;
    borderStyle?: string;
    borderRadius?: string;
    rowSeperatorStyle?: TableRowSeperatorStyle;
    showHeader?: boolean;
    minimumHeaderHeight?: number;
    minimumRowHeight?: number;
    rowFontSize?: string;
    enablePaging?: boolean;
    pageSize?: number;
    emptyTableText?: string;
    emptyTableTextAlign?: 'left' | 'center' | 'right' | string;
}> = ({
    children,
    data,
    headerTheme = TableHeaderTheme.Transparent,
    rowTheme = TableRowTheme.Transparent,
    width = 'auto',
    height = 'auto',
    borderStyle = TableBorderStyle.None,
    borderRadius = TableBorderRadius.None,
    rowSeperatorStyle = TableRowSeperatorStyle.Small,
    showHeader = true,
    minimumHeaderHeight,
    minimumRowHeight,
    rowFontSize = '14px',
    enablePaging = false,
    pageSize = 10,
    emptyTableText,
    emptyTableTextAlign = 'center',
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const pagedData = useMemo(() => {
        if (!enablePaging) return data;
        const start = (currentPage - 1) * pageSize;
        return data.slice(start, start + pageSize);
    }, [data, enablePaging, currentPage, pageSize]);
    const totalPages = enablePaging ? Math.ceil(data.length / pageSize) : 1;
    const totalItems = data.length;

    // Compute the 5-page window around the current page
    const getPageWindow = () => {
        if (!enablePaging) return [];

        const windowSize = 5;
        const half = Math.floor(windowSize / 2);

        let start = currentPage - half;
        let end = currentPage + half;

        // Clamp to valid range
        if (start < 1) {
            end += 1 - start;
            start = 1;
        }
        if (end > totalPages) {
            start -= end - totalPages;
            end = totalPages;
        }

        start = Math.max(1, start);
        end = Math.min(totalPages, end);

        const pages = [];
        for (let p = start; p <= end; p++) pages.push(p);
        return pages;
    };

    const columns = React.Children.toArray(children)
        .filter((child): child is ReactElement<TableColumnProps> => isValidElement(child) && child.type === TableColumn)
        .map((child) => ({
            key: child.props.columnKey as string,
            header: child.props.header,
            width: child.props.width ?? 'auto',
            visible: child.props.visible ?? 'true',
            showHeaderOnWrap: child.props.showHeaderOnWrap ?? 'true',
            actionColumn: child.props.actionColumn,
            center: child.props.center ?? false,
        }));

    return (
        <>
            <div
                className={`table-container ${borderRadius} ${borderStyle}`}
                style={{
                    width,
                    height,
                }}
            >
                {showHeader && (
                    <div className={`table-row header-row ${headerTheme} ${borderRadius}`}>
                        {columns.map((col, index) => (
                            <div
                                key={index}
                                className={`table-cell ${col.actionColumn ? 'actionColumn' : ''}`}
                                style={{
                                    flexBasis: col.width,
                                    display: col.visible ? 'visible' : 'none',
                                    textAlign: col.center ? 'center' : undefined,
                                    minHeight: minimumHeaderHeight ? `${minimumRowHeight}px` : undefined,
                                    justifyContent: col.center ? 'center' : undefined,
                                }}
                            >
                                {col.header}
                            </div>
                        ))}
                    </div>
                )}

                {data.length === 0 && emptyTableText && (
                    <div
                        className={`table-row ${rowTheme}`}
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            display: 'flex',
                            justifyContent:
                                emptyTableTextAlign === 'left'
                                    ? 'flex-start'
                                    : emptyTableTextAlign === 'right'
                                      ? 'flex-end'
                                      : emptyTableTextAlign,
                            alignItems: 'center',
                            minHeight: minimumRowHeight ? `${minimumRowHeight}px` : '40px',
                            fontSize: rowFontSize,
                            paddingLeft: emptyTableTextAlign === 'left' ? '2rem' : undefined,
                            paddingRight: emptyTableTextAlign === 'right' ? '2rem' : undefined,
                            paddingTop: '1rem',
                            paddingBottom: '1rem',
                        }}
                    >
                        {emptyTableText}
                    </div>
                )}

                {pagedData.length > 0 &&
                    pagedData.map((row, rowIndex) => {
                        return (
                            <div
                                key={rowIndex}
                                className={`table-row ${rowTheme} ${data.length == 1 ? TableRowSeperatorStyle.None : rowSeperatorStyle}`}
                                style={{
                                    fontSize: rowFontSize ? rowFontSize : '',
                                    backgroundColor: row.rowColor ?? undefined,
                                    minHeight: minimumRowHeight ? `${minimumRowHeight}px` : undefined,
                                    display: 'flex',
                                }}
                            >
                                {columns.map((col, colIndex) => (
                                    <div
                                        key={colIndex}
                                        className={`table-cell ${col.actionColumn ? 'actionColumn' : ''}`}
                                        style={{
                                            flexBasis: col.width,
                                            display: col.visible ? 'visible' : 'none',
                                            textAlign: col.center ? 'center' : undefined,
                                            justifyContent: col.center ? 'center' : undefined,
                                            alignItems: col.center ? 'center' : undefined,
                                            minHeight: minimumRowHeight ? `${minimumRowHeight}px` : undefined,
                                        }}
                                    >
                                        <div
                                            className='cell-header-label'
                                            style={{ display: col.showHeaderOnWrap && col.header ? 'visible' : 'none' }}
                                        >
                                            {col.header}:
                                        </div>
                                        <div className='cell-value'>{row[col.key] ?? ''}</div>
                                    </div>
                                ))}
                            </div>
                        );
                    })}
            </div>
            {enablePaging && totalPages > 1 && (
                <div
                    className='table-paging-controls'
                    style={{
                        marginTop: '10px',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '10px',
                        width: '100%', // <-- ensures true centering
                    }}
                >
                    <span>
                        Page {currentPage} of {totalPages} ({totalItems} items)
                    </span>
                    <Button
                        id='tablePagePrevButton'
                        theme={ButtonTheme.LinkBlue}
                        disabled={currentPage === 1}
                        handleClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    >
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </Button>
                    {/* NEW: Page number buttons */}
                    {getPageWindow().map((page) => (
                        <Button
                            id={`tablePage${page}`}
                            theme={page === currentPage ? ButtonTheme.HighlightedGrey : ButtonTheme.LinkBlue}
                            handleClick={() => setCurrentPage(page)}
                            width='16px'
                            height='20px'
                            borderRadius={ButtonBorderRadius.Small}
                        >
                            {page}
                        </Button>
                    ))}
                    <Button
                        id='tablePageNextButton'
                        theme={ButtonTheme.LinkBlue}
                        disabled={currentPage === totalPages}
                        handleClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    >
                        <FontAwesomeIcon icon={faChevronRight} />
                    </Button>
                </div>
            )}
        </>
    );
};
